import React from "react";
import "./experience.css";
import Reveal from "../motion/Reveal";
import { motion, useReducedMotion } from "framer-motion";
import { slideIn, stagger } from "../../motion";

const roles = [
  {
    company: "Solution Circle",
    badge: "Current",
    role: "React Native Developer and IT Project Manager",
    type: "Full-time · On-site",
    location: "Riyadh, Saudi Arabia",
    period: "Sep 2024 — Present",
    summary:
      "Experienced React Native developer building and maintaining mobile, web, and desktop applications. Leading development teams, managing project lifecycles, collaborating with UI/UX and backend, conducting code reviews, and delivering scalable solutions with Agile/Scrum.",
    bullets: [
      "Timeloop — cross-platform productivity and time-tracking for Windows, macOS, Linux, and mobile with Electron.js and React Native.",
      "Eventy — event management with registration, scheduling, real-time notifications, chat, and calendar (React Native, React.js, Firebase, Redux Toolkit).",
      "HR App — attendance, check-in/out, leave management, geolocation, geofencing, and Firebase push notifications.",
      "Eventy OCR — business card scanning with Google Vision API and automated lead creation.",
      "Eventy Engage — lead and staff management with OCR sync for automated lead generation on the show floor.",
    ],
    stack: [
      "React Native",
      "React.js",
      "Electron.js",
      "Python",
      "Firebase",
      "Redux Toolkit",
      "Google Vision API",
      "PostgreSQL",
    ],
  },
  {
    company: "Valleyways Tech",
    role: "React Native Developer",
    type: "Full-time · Hybrid",
    location: "Islamabad, Pakistan",
    period: "Feb 2024 — Aug 2024",
    summary:
      "Built cross-platform mobile applications with responsive UI and strong performance in a hybrid remote/on-site team.",
    bullets: [
      "Integrated REST APIs and managed app state with Redux Toolkit.",
      "Implemented push notifications, deep linking, and app analytics.",
      "Contributed to hybrid collaboration that improved delivery speed.",
      "Participated in user testing and fixed production issues.",
    ],
    stack: ["React Native", "Redux", "Android SDK", "Firebase", "REST API", "Git"],
  },
  {
    company: "TechCreator",
    role: "React Native | Angular | React | Node.js",
    type: "Full-time · On-site",
    location: "Swabi District, Khyber Pakhtunkhwa, Pakistan",
    period: "Oct 2021 — Aug 2023",
    summary:
      "Developed and maintained full-stack applications — React Native for mobile, Angular for web, and Node.js backends.",
    bullets: [
      "Designed RESTful APIs and backend services with Node.js and Express.",
      "Built real-time chat and notifications with Socket.IO.",
      "Conducted unit testing and code reviews for maintainability.",
      "Improved app loading times and reduced API response delays.",
    ],
    stack: ["React Native", "React", "Angular", "Node.js", "MongoDB", "Express", "Socket.IO"],
  },
  {
    company: "Stackpush Technology",
    role: "React Native Developer",
    type: "Part-time · Remote",
    location: "India",
    period: "Sep 2022 — Nov 2022",
    summary:
      "Part-time remote React Native work focused on UI modules, animations, and third-party integrations.",
    bullets: [
      "Built UI components, animations, and third-party library integrations.",
      "Debugged and resolved issues across Android and iOS.",
      "Collaborated via GitHub and Trello; delivered production components on a short cycle.",
    ],
    stack: ["React Native", "JavaScript", "Git", "Trello", "REST APIs"],
  },
];

const Experience = () => {
  const reduce = useReducedMotion();

  return (
    <section id="experience" className="section experience">
      <div className="container">
        <Reveal>
          <p className="section__label">
            <span>03</span> — Experience
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="section__title">Where I&apos;ve worked.</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="section__lead">
            Four full-time roles across Pakistan and Saudi Arabia — one ongoing in Riyadh.
          </p>
        </Reveal>

        <motion.ol
          className="experience__list"
          variants={stagger(0.1)}
          initial={reduce ? false : "hidden"}
          whileInView="show"
          viewport={{ once: true, amount: 0.12 }}
        >
          {roles.map((job) => (
            <motion.li
              key={`${job.company}-${job.period}`}
              className="experience__item"
              variants={slideIn}
            >
              <div className="experience__head">
                <div>
                  <h3>
                    {job.company}
                    {job.badge && <span>{job.badge}</span>}
                  </h3>
                  <p className="experience__role">{job.role}</p>
                  <p className="experience__meta">
                    {job.type}
                    {job.location ? ` · ${job.location}` : ""}
                  </p>
                </div>
                <p className="mono experience__period">{job.period}</p>
              </div>
              <p className="experience__summary">{job.summary}</p>
              <ul className="experience__bullets">
                {job.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <ul className="experience__stack">
                {job.stack.map((tech) => (
                  <li key={tech}>{tech}</li>
                ))}
              </ul>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
};

export default Experience;
